/**
 * Payor aggregation service (US-033.3).
 *
 * Per-athlete × per-payor × per-tax-year sum of cash-equivalent across
 * every compensation item on every one of the athlete's deals with this
 * payor. The NIL Go $600 aggregate rule fires when this number crosses
 * the threshold — even if no individual deal does — so the readiness
 * service uses this to flip a deal out of `not_reportable` even when
 * its own total is small.
 *
 * "Tax year" = the calendar year of the deal's `signedDate` if set,
 * else `startDate`, else `createdAt`. We pick the most truthful
 * "when did this deal happen" anchor available — NIL Go reports income
 * in the year the activity occurred, not when the row was last edited.
 *
 * Pure-ish: takes a db handle + ids, runs ONE prisma query, returns a
 * number. No mutations, no analytics events.
 *
 * @package @app/api
 */

// biome-ignore lint/suspicious/noExplicitAny: Prisma client type erased at boundary.
type Db = any;

export interface PayorAggregateExposureInput {
  db: Db;
  athleteId: string;
  payorId: string;
  /** Year to sum across. Defaults to the current calendar year. */
  taxYear?: number;
  /** When provided, exclude this deal from the sum — useful when previewing
   * what the exposure WAS before the deal in question, so the over-threshold
   * banner can say "this deal pushed you over". Omit to include every deal. */
  excludeDealId?: string;
}

export interface PayorAggregateExposure {
  /** Total cash-equivalent across all qualifying deals + items in the year. */
  total: number;
  /** Deals that contributed to the total. Ordered by anchor date asc. The UI
   * uses this list for the "earlier deals can be submitted now" workflow. */
  deals: Array<{
    id: string;
    title: string;
    anchorDate: string; // ISO
    cashEquivalent: number;
  }>;
  taxYear: number;
}

/** The anchor date a deal contributes against. See the docstring. */
function pickAnchorDate(deal: { signedDate: Date | null; startDate: Date | null; createdAt: Date }): Date {
  return deal.signedDate ?? deal.startDate ?? deal.createdAt;
}

export async function computePayorAggregateExposure({
  db,
  athleteId,
  payorId,
  taxYear,
  excludeDealId,
}: PayorAggregateExposureInput): Promise<PayorAggregateExposure> {
  const year = taxYear ?? new Date().getUTCFullYear();
  const start = new Date(Date.UTC(year, 0, 1));
  const end = new Date(Date.UTC(year + 1, 0, 1));

  // Pull every deal between this athlete + payor whose anchor date falls
  // in the target year. We over-fetch slightly (filtering on `createdAt`
  // OR `startDate` OR `signedDate` would require an OR clause that
  // Prisma can't index well) by pulling on createdAt window AND
  // post-filtering — the per-athlete×payor cardinality is small enough
  // (single-digit deals typically) that this is fine.
  const deals = await db.deal.findMany({
    where: {
      athleteId,
      payorId,
      // Cheap pre-filter — the post-filter below is what actually decides.
      // Captures deals whose anchor date might land in the year even when
      // createdAt sits in the prior year (e.g. an old draft signed in
      // the current year).
      OR: [
        { createdAt: { gte: start, lt: end } },
        { startDate: { gte: start, lt: end } },
        { signedDate: { gte: start, lt: end } },
      ],
    },
    include: { compensationItems: true },
  });

  const contributing: PayorAggregateExposure['deals'] = [];
  let total = 0;
  for (const d of deals) {
    if (excludeDealId && d.id === excludeDealId) continue;
    const anchor = pickAnchorDate(d);
    if (anchor < start || anchor >= end) continue;
    const cashEquivalent = (d.compensationItems ?? []).reduce(
      (s: number, it: { amount: unknown }) => s + Number(it.amount ?? 0),
      0,
    );
    total += cashEquivalent;
    contributing.push({
      id: d.id,
      title: d.title,
      anchorDate: anchor.toISOString(),
      cashEquivalent,
    });
  }
  contributing.sort((a, b) => a.anchorDate.localeCompare(b.anchorDate));

  return { total, deals: contributing, taxYear: year };
}

/**
 * Deals with this payor that an athlete *still owes* NIL Go a submission
 * for — across **every** tax year, not just the current calendar one.
 *
 * NIL Go's $600 rule is per tax year: a 2025 deal owes a 2025 submission
 * if 2025 aggregate with the payor crossed $600. The athlete doesn't
 * stop owing it just because the calendar year rolled over. The banner
 * on the readiness panel must therefore look across all years.
 *
 * For each anchor year present in the athlete's deals with this payor:
 *   - sum the year's cash-equivalent;
 *   - if it crossed the threshold, include every deal in that year that
 *     has no live `NilGoSubmission` row (a `cancelled` row means the
 *     athlete withdrew, so the deal is back to "not submitted").
 *
 * Ordering: ascending by anchor date so the UI shows the oldest
 * unsubmitted deal first.
 */
export interface UnsubmittedReportableDeal {
  id: string;
  title: string;
  anchorDate: string;
  anchorYear: number;
  cashEquivalent: number;
}

export async function computeUnsubmittedReportableDealsForPayor({
  db,
  athleteId,
  payorId,
}: {
  db: Db;
  athleteId: string;
  payorId: string;
}): Promise<UnsubmittedReportableDeal[]> {
  const deals = await db.deal.findMany({
    where: { athleteId, payorId },
    include: { compensationItems: true },
  });
  if (deals.length === 0) return [];

  const submissions = await db.nilGoSubmission.findMany({
    where: {
      dealId: { in: deals.map((d: { id: string }) => d.id) },
      status: { not: 'cancelled' },
    },
    select: { dealId: true },
  });
  const submittedSet = new Set(submissions.map((s: { dealId: string }) => s.dealId));

  type Bucket = { cash: number; anchor: Date; deal: (typeof deals)[number] };
  const byYear = new Map<number, Bucket[]>();
  for (const d of deals) {
    const anchor = pickAnchorDate(d);
    const year = anchor.getUTCFullYear();
    const cash = (d.compensationItems ?? []).reduce(
      (s: number, it: { amount: unknown }) => s + Number(it.amount ?? 0),
      0,
    );
    const bucket = byYear.get(year) ?? [];
    bucket.push({ cash, anchor, deal: d });
    byYear.set(year, bucket);
  }

  const NIL_GO_THRESHOLD_USD = 600;
  const result: UnsubmittedReportableDeal[] = [];
  for (const [year, bucket] of byYear) {
    const yearTotal = bucket.reduce((s, b) => s + b.cash, 0);
    if (yearTotal < NIL_GO_THRESHOLD_USD) continue;
    for (const b of bucket) {
      if (submittedSet.has(b.deal.id)) continue;
      result.push({
        id: b.deal.id,
        title: b.deal.title,
        anchorDate: b.anchor.toISOString(),
        anchorYear: year,
        cashEquivalent: b.cash,
      });
    }
  }
  result.sort((a, b) => a.anchorDate.localeCompare(b.anchorDate));
  return result;
}
